/**
 * Post-processing stack (PRD §3.4, §6.3, P2).
 *
 * Wraps `@react-three/postprocessing`'s `EffectComposer` with the
 * effects the PRD names by hand:
 *   - ACES Filmic tone mapping (set on the renderer in `Stage`)
 *   - Bloom on emissives
 *   - N8AO (replaces SSAO) for cheaper, sharper AO in voxel seams
 *   - Smooth subtle vignette to anchor the framing
 *   - ChromaticAberration pulse on kill / hit (B1)
 *
 * Toggleable per-effect so stories can A/B the contribution of each.
 * If you suspect a perf problem, disable Bloom/N8AO first — they're
 * the heaviest two passes.
 */

import { useEffect, useMemo, useRef } from "react";
import { useFrame } from "@react-three/fiber";
import {
  Bloom,
  ChromaticAberration,
  EffectComposer,
  N8AO,
  ToneMapping,
} from "@react-three/postprocessing";
import { ToneMappingMode, VignetteEffect } from "postprocessing";
import { Vector2 } from "three";

export interface PostFxProps {
  bloom?: boolean;
  ssao?: boolean;
  vignette?: boolean;
  tonemap?: boolean;
  /** Rising-edge `performance.now()` to drive the FE-30 Q4 vignette
   *  pulse (focus-side took a hit). `null` ⇒ no pulse. */
  vignettePulseStartedAt?: number | null;
  /** Rising-edge `performance.now()` to drive the B1 chromatic
   *  aberration spike on a kill landed by the focus side. */
  killPulseStartedAt?: number | null;
  /** Rising-edge `performance.now()` to drive the B1 chromatic
   *  aberration micro-spike on a hit landed by the focus side. */
  hitPulseStartedAt?: number | null;
}

const VIGNETTE_BASE_DARKNESS = 0.6;
const VIGNETTE_PULSE_PEAK_DARKNESS = 1.0;
const VIGNETTE_PULSE_DURATION_MS = 400;

// B1 chromatic aberration peaks. Values are in normalized screen-space
// offsets (×screen). Kill pulse is ~3× the hit pulse so the moment
// reads as drama. Both decay over their own window.
const CA_KILL_PEAK = 0.0045;
const CA_KILL_DURATION_MS = 520;
const CA_HIT_PEAK = 0.0015;
const CA_HIT_DURATION_MS = 220;
const CA_BASE = 0.00015; // tiny baseline so emissives don't look CG-clean

export function PostFx({
  bloom = true,
  ssao = true,
  vignette = true,
  tonemap = true,
  vignettePulseStartedAt = null,
  killPulseStartedAt = null,
  hitPulseStartedAt = null,
}: PostFxProps) {
  const vignetteEffect = useMemo(
    () =>
      new VignetteEffect({
        eskil: false,
        offset: 0.18,
        darkness: VIGNETTE_BASE_DARKNESS,
      }),
    [],
  );
  // Mutable Vector2 referenced by ChromaticAberration's `offset` prop.
  // We mutate in-place each frame to avoid React state churn.
  const caOffset = useMemo(() => new Vector2(CA_BASE, CA_BASE), []);
  const prevCaSpikeRef = useRef<number>(CA_BASE);

  useEffect(() => () => vignetteEffect.dispose(), [vignetteEffect]);

  useFrame(() => {
    const now = performance.now();

    // Vignette pulse — unchanged.
    if (vignettePulseStartedAt === null) {
      vignetteEffect.darkness = VIGNETTE_BASE_DARKNESS;
    } else {
      const elapsed = now - vignettePulseStartedAt;
      if (elapsed < 0 || elapsed >= VIGNETTE_PULSE_DURATION_MS) {
        vignetteEffect.darkness = VIGNETTE_BASE_DARKNESS;
      } else {
        const t = elapsed / VIGNETTE_PULSE_DURATION_MS;
        const tri = t < 0.5 ? t * 2 : (1 - t) * 2;
        vignetteEffect.darkness =
          VIGNETTE_BASE_DARKNESS +
          (VIGNETTE_PULSE_PEAK_DARKNESS - VIGNETTE_BASE_DARKNESS) * tri;
      }
    }

    // B1 — chromatic aberration. Max of the two active pulses so a
    // hit landing during a kill window doesn't reduce the spike.
    let ca = CA_BASE;
    if (killPulseStartedAt !== null) {
      const e = now - killPulseStartedAt;
      if (e >= 0 && e < CA_KILL_DURATION_MS) {
        const t = e / CA_KILL_DURATION_MS;
        // Fast attack (20%) + cosine decay — same vocabulary as
        // hit/recoil impulses.
        const env =
          t < 0.2 ? t / 0.2 : 0.5 * (1 + Math.cos(((t - 0.2) / 0.8) * Math.PI));
        ca = Math.max(ca, CA_BASE + CA_KILL_PEAK * env);
      }
    }
    if (hitPulseStartedAt !== null) {
      const e = now - hitPulseStartedAt;
      if (e >= 0 && e < CA_HIT_DURATION_MS) {
        const t = e / CA_HIT_DURATION_MS;
        const env =
          t < 0.2 ? t / 0.2 : 0.5 * (1 + Math.cos(((t - 0.2) / 0.8) * Math.PI));
        ca = Math.max(ca, CA_BASE + CA_HIT_PEAK * env);
      }
    }
    if (ca !== prevCaSpikeRef.current) {
      caOffset.set(ca, ca);
      prevCaSpikeRef.current = ca;
    }
  });

  return (
    <EffectComposer multisampling={4}>
      {ssao ? (
        // N8AO — sharper AO in voxel seams than the old SSAO pass,
        // at lower cost (no separate normal pass).
        <N8AO aoRadius={0.35} intensity={2.2} quality="medium" />
      ) : (
        <></>
      )}
      {bloom ? (
        <Bloom
          intensity={0.8}
          luminanceThreshold={0.65}
          luminanceSmoothing={0.2}
          mipmapBlur
        />
      ) : (
        <></>
      )}
      <ChromaticAberration offset={caOffset} radialModulation={false} modulationOffset={0} />
      {vignette ? <primitive object={vignetteEffect} /> : <></>}
      {tonemap ? <ToneMapping mode={ToneMappingMode.ACES_FILMIC} /> : <></>}
    </EffectComposer>
  );
}
